import * as lodash from 'lodash';

import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewContainerRef,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { CarouselModule } from 'primeng/carousel';

import { COLORS, ColorPalette } from '@styles/colors';

import { InternalChartComponent } from '@cxc/ui-kit/chart.js/components/internal-chart';
import { DATA_TYPE } from '@cxc/ui-kit/chart.js/const-enums';
import { decodeChart } from '@cxc/ui-kit/chart.js/decode';
import { Chart } from '@cxc/ui-kit/chart.js/models';
import type { DoughnutCenterTotal } from '@cxc/ui-kit/chart.js/plugins';

import { SelectableWidget } from '@components/selectable-widget/selectable-widget.component';

import { CURRENCY, CURRENCY_SYMBOLS } from '@cxc/core/enums';

import { CurrencifyPipe } from '@cxc/ui-kit/pipes';

import { CurrencyState } from '@services/state/currency-state/currency.state';
import { OverviewResumeState } from '@services/state/overview-resume-state/overview-resume.state';

import { formatCurrencyAmount } from '@cxc/core/utils';
import { knownCurrencyOr } from '@utils/format-money';
import { arraySignal } from '@utils/signals/arraySignal';

// Backend-stored title (core_metrics_snapshots.py's
// `map_operations_to_doughnut_chart`), matched normalized
// (trim/lowercase) -- NOT a stable key. A rewording on that side silently
// kills this feature with no red test on either repo (MR review S3); a
// contract test below pins the exact literal so a mismatch fails loudly
// here instead. Worth a follow-up: ask the backend to carry a stable key
// (e.g. `SnapshotsChartType.TOTAL_CREDIT_AMOUNT`, already internal to
// that repo) alongside the human-readable title.
const MAXIMUM_CREDIT_AMOUNT_TITLE = 'maximum credit amount';

// Local extension: `doughnutCenterTotal` is a purely frontend-computed,
// pre-formatted value (LEN-2176) -- it has no business on the shared
// `Chart.Internal.Chart` model, which only carries backend wire fields.
type CarouselChart = Chart.Internal.Chart & {
  doughnutCenterTotal?: DoughnutCenterTotal;
};

@Component({
  selector: 'graphs-carousel',
  standalone: true,
  imports: [InternalChartComponent, CarouselModule, SelectableWidget],
  templateUrl: './graphs-carousel.component.html',
  styleUrl: './graphs-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GraphsCarousel {
  // Expose to HTML
  ChartType = Chart.Type;

  // Angular core
  destroyRef = inject(DestroyRef);
  viewContainerRef = inject(ViewContainerRef);

  // States
  private overviewResumeState = inject(OverviewResumeState);
  private currencyState = inject(CurrencyState);

  /**
   * The currency the carousel formats its amounts in (LEN-2465): the one the
   * loaded resume was converted to. `selection` only moves when its payload
   * lands, so the figures and their format never disagree mid-fetch.
   */
  public displayCurrency = computed(() =>
    knownCurrencyOr(
      this.overviewResumeState.selection()?.currency,
      CURRENCY.MXN
    )
  );

  private readonly currencifyPipe = new CurrencifyPipe();

  // Export inputs/outputs
  exportMode = input<boolean>(false);
  selectedWidgets = input<HTMLElement[]>([]);
  widgetToggle = output<{ checked: boolean; el: HTMLElement }>();

  // Charts
  chartCarousel = arraySignal<CarouselChart>([]);

  isWidgetSelected(el: HTMLElement): boolean {
    return this.selectedWidgets().includes(el);
  }

  constructor() {
    // The request lives in OverviewResumeState; this component only reads the
    // carousel slice of the shared payload.
    // See facilities.component.ts: the undefined emitted on a selection change
    // has to clear the carousel, or it keeps showing the previous lender's
    // charts (LEN-2078). decodeChartCarousel replaces the whole signal, so an
    // empty list is the clear.
    toObservable(this.overviewResumeState.carrouselCharts)
      .pipe(takeUntilDestroyed())
      .subscribe(charts => this.decodeChartCarousel(charts ?? []));
  }

  decodeChartCarousel(apiCharts: Chart.V1.Chart[]) {
    const newCarousel: CarouselChart[] = [];

    apiCharts.forEach(apiChart => {
      const chartName = apiChart.title.toUpperCase();
      let colors: ColorPalette[] | undefined = undefined;

      // Derived from the payload, not the chart's title (MR review S1):
      // whichever charts tag their datasets with a monetary yAxisID
      // (LEN-2217, facilities-api!874) already get their currency symbol
      // from the tooltip component's own switch on that field --
      // `valueIsCurrency: true` would prepend it a second time via
      // `currencify` (MR review B1). Reacts automatically the day another
      // chart in this carousel gains a monetary yAxisID, instead of this
      // guard silently going stale. Same reasoning Cash Flow's
      // collection-chart/payment-history components already apply.
      const hasMonetaryAxis = (apiChart.data?.datasets ?? []).some(
        dataset =>
          dataset.yAxisID === DATA_TYPE.MONEY_LINEAR ||
          dataset.yAxisID === DATA_TYPE.MONETARY
      );

      if (chartName.includes('AGING')) {
        colors = [COLORS.secondary];
      } else if (chartName.includes('COLLECTION')) {
        colors = [COLORS.zinc, COLORS.secondary];
      } else if (chartName.includes('ASSETS')) {
        colors = [COLORS.secondary];
      } else if (chartName.includes('PAYMENT HISTORY')) {
        colors = [COLORS.secondary, COLORS.zinc];
      }

      const internalChart: CarouselChart = decodeChart(
        apiChart,
        this.destroyRef,
        this.viewContainerRef,
        {
          colors: colors,
          currency: this.displayCurrency(),
          tooltip: {
            valueIsCurrency: !hasMonetaryAxis,
            hideOnZeros: true,
            title: {
              autogenerate: true,
            },
          },
        }
      );

      internalChart.doughnutCenterTotal =
        this.doughnutCenterTotalFor(internalChart);

      if (chartName.includes('ASSETS')) {
        lodash.merge(internalChart.options.scales, {
          x: {
            ticks: {
              stepSize: (internalChart.options.scales as any).x.max / 2,
              // LEN-2465: in the resume's currency, not a hard-coded `$`
              // on an en-US number.
              callback: (t: number) =>
                formatCurrencyAmount(t, this.displayCurrency()),
            },
          },
          percentage: {
            display: true,
            type: 'linear',
            position: 'top',
            max: 100,
            ticks: {
              maxTicksLimit: 3,
              callback: (str: string) => str + '%',
            },
          },
        });
      } else if (chartName.includes('AGING')) {
        const labels: string[] = internalChart.data?.labels ?? [];
        const modifiedLabels = labels.map((label, idx) =>
          idx === 0 ? label : `${label} days`
        );
        internalChart.data.labels = modifiedLabels;
      }

      newCarousel.push(internalChart);
    });

    this.chartCarousel.set(newCarousel);
  }

  // LEN-2176: the lender-level "Maximum credit amount" doughnut (one
  // segment per operation/facility) shows its real total in the center --
  // facilities-api's ChartConfig.grand_total_amount, set by
  // CoreMetricsSnapshotsService.map_operations_to_doughnut_chart. Absent
  // (older backend) means no total shown, not a wrong one.
  doughnutCenterTotalFor(
    chart: CarouselChart
  ): DoughnutCenterTotal | undefined {
    if (chart.title?.trim().toLowerCase() !== MAXIMUM_CREDIT_AMOUNT_TITLE) {
      return undefined;
    }

    if (chart.grand_total_amount == null) {
      return undefined;
    }

    // `currencySelected()?.currency` is OUT_OF_STANDARD_CURRENCY, which also
    // allows `EU`/`L` -- values CURRENCY_SYMBOLS (and CurrencifyPipe's
    // CURRENCY param) don't cover. Bail rather than render `undefined1.2M`
    // with `EU`/`L` as the ISO code (same guard Concentrations' equivalent
    // method already applies, LEN-2176).
    const currency = this.currencyState.currencySelected()?.currency;
    if (!currency || !(currency in CURRENCY_SYMBOLS)) {
      return undefined;
    }

    return {
      value: this.currencifyPipe.transform(
        chart.grand_total_amount,
        currency as unknown as CURRENCY
      ) as string,
      code: currency,
    };
  }
}
